// src/components/SolanaPaymentModal.js

import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  Box,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { LAMPORTS_PER_SOL, Transaction, SystemProgram, PublicKey } from '@solana/web3.js';
import axios from 'axios';
import PropTypes from 'prop-types';
import { WalletMultiButton, useWalletModal } from '@solana/wallet-adapter-react-ui';
import { AuthContext } from '../context/AuthContext'; // Import AuthContext

const SolanaPaymentModal = ({
  open,
  handleClose,
  amount,
  recipient,
  onPaymentSuccess,
  onPaymentFailure,
  paymentReason,
  selectedGuild,
  collectionBeingUnlocked,
  subscriptionLevel,
  maxCollectionsAllowed,
}) => {
  console.log('SolanaPaymentModal props:', {
    open,
    handleClose,
    amount,
    recipient,
    onPaymentSuccess,
    onPaymentFailure,
    paymentReason,
    selectedGuild,
    collectionBeingUnlocked,
    subscriptionLevel,
    maxCollectionsAllowed,
  });

  const { publicKey, connected, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal(); // Manage wallet modal visibility
  const { connection } = useConnection();
  const theme = useTheme();

  // Access tokens and discordUserId from AuthContext
  const { accessToken, discordUserId } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);

  // Snackbar state for success/error messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleSelectWallet = () => {
    // Close the payment modal temporarily
    handleClose();
    // Open the wallet selection modal
    setVisible(true); // Trigger the wallet modal
  };

  const handlePayment = useCallback(async () => {
    if (!connected || !publicKey) {
      setSnackbarMessage('Wallet not connected.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!accessToken) {
      setSnackbarMessage('Authentication token missing. Please log in again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      handleClose();
      return;
    }

    setLoading(true);

    try {
      console.log('Preparing transaction...');
      const lamports = amount * LAMPORTS_PER_SOL;
      const recipientPubKey = new PublicKey(recipient);

      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: recipientPubKey,
          lamports,
        })
      );

      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      console.log('Sending transaction...');
      const signature = await sendTransaction(transaction, connection);
      console.log('Transaction signature:', signature);

      // Wait for confirmation
      console.log('Waiting for transaction confirmation...');
      const confirmation = await connection.confirmTransaction(signature, 'confirmed');
      console.log('Transaction confirmation:', confirmation);

      if (confirmation.value.err) throw new Error('Transaction failed');

      // Prepare the transaction details
      const transactionDetails = {
        signature,
        amount,
        payer: publicKey.toBase58(),
        recipient,
        paymentReason,
        guildId: selectedGuild,
        discordUserId, // Include discordUserId
      };

      // Include collectionAddress if paymentReason is 'unlock_collection'
      if (paymentReason === 'unlock_collection' && collectionBeingUnlocked) {
        transactionDetails.collectionAddress = collectionBeingUnlocked.collectionAddress;
      }

      // Construct the API payload based on paymentReason
      let apiEndpoint = '';
      let apiPayload = {};

      if (paymentReason === 'unlock_collection') {
        apiEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections/unlock`;
        apiPayload = {
          transactionSignature: signature,
          collectionAddress: transactionDetails.collectionAddress,
          payer: transactionDetails.payer,
          recipient: transactionDetails.recipient,
          discordUserId, // Include discordUserId
        };
      } else if (paymentReason === 'additional_collection_slot') {
        apiEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/purchase-collection-slot`;
        apiPayload = {
          transactionSignature: signature,
          amount,
          payer: transactionDetails.payer,
          recipient: transactionDetails.recipient,
          slotsToPurchase: 1, // Adjust as needed
          discordUserId, // Include discordUserId
        };
      } else if (paymentReason === 'subscription') {
        apiEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/update-subscription`;
        apiPayload = {
          guildId: selectedGuild,
          subscriptionExpiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
          subscriptionLevel: subscriptionLevel.toLowerCase(),
          paymentHash: signature,
          amount,
          payer: transactionDetails.payer,
          recipient: transactionDetails.recipient,
          discordUserId, // Include discordUserId
        };
      } else {
        throw new Error('Invalid payment reason');
      }

      console.log('Sending API Request:', { apiEndpoint, apiPayload });

      // Call the appropriate API endpoint with Authorization header
      await axios.post(apiEndpoint, apiPayload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Display success message in Snackbar
      setSnackbarMessage('Payment successful!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setLoading(false);

      handleClose();
      onPaymentSuccess(transactionDetails);
    } catch (error) {
      console.error('Payment error:', error);
      let errorMessage = 'An unexpected error occurred during payment.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbarMessage(`Payment failed: ${errorMessage}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      onPaymentFailure(error);
    } finally {
      setLoading(false);
    }
  }, [
    connected,
    publicKey,
    accessToken,
    discordUserId,
    connection,
    sendTransaction,
    amount,
    recipient,
    paymentReason,
    selectedGuild,
    handleClose,
    onPaymentSuccess,
    onPaymentFailure,
    collectionBeingUnlocked,
    subscriptionLevel,
  ]);

  // Determine payment message based on payment reason
  let paymentMessage;
  if (paymentReason === 'unlock_collection') {
    paymentMessage = `You're about to pay ${amount} SOL to unlock this collection. This will allow you to modify or delete the collection.`;
  } else if (paymentReason === 'additional_collection_slot') {
    paymentMessage = `You're about to pay ${amount} SOL to purchase an additional collection slot.`;
  } else if (paymentReason === 'subscription') {
    paymentMessage = `You're about to pay ${amount} SOL to subscribe to the '${subscriptionLevel}' package. This will grant you additional benefits.`;
  } else {
    paymentMessage = `You're about to make a payment of ${amount} SOL.`;
  }

  // Modal styles to match branding
  const modalStyles = {
    dialogPaper: { backgroundColor: '#040813', color: '#E0E4ED' },
    title: { backgroundColor: '#0D1F3A', color: '#E0E4ED', padding: theme.spacing(2) },
    content: { padding: theme.spacing(2), backgroundColor: '#040813' },
    actions: { padding: theme.spacing(1), backgroundColor: '#040813' },
    payButton: {
      backgroundColor: '#728C63',
      color: '#FFFFFF',
      fontFamily: 'Poppins, sans-serif',
      '&:hover': { backgroundColor: '#8BA675' },
    },
    cancelButton: { color: '#FFFFFF', fontFamily: 'Poppins, sans-serif' },
    walletButton: {
      backgroundColor: '#728C63',
      color: '#FFFFFF',
      width: '100%',
      marginTop: '20px',
      fontFamily: 'Poppins, sans-serif',
      '&:hover': {
        backgroundColor: '#8BA675',
      },
    },
  };

  return (
    <>
      {/* Payment Modal */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={modalStyles.title}>Payment Required</DialogTitle>
        <DialogContent sx={modalStyles.content}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Typography>{paymentMessage}</Typography>
            {!connected ? (
              <>
                <Button
                  onClick={handleSelectWallet}
                  variant="contained"
                  sx={modalStyles.walletButton}
                >
                  Select Wallet
                </Button>
                <WalletMultiButton style={{ display: 'none' }} />
              </>
            ) : (
              <Button
                onClick={handlePayment}
                disabled={loading}
                variant="contained"
                sx={modalStyles.payButton}
              >
                {loading ? <CircularProgress size={24} /> : 'Pay'}
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={modalStyles.actions}>
          <Button onClick={handleClose} sx={modalStyles.cancelButton}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Success and Error Messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

SolanaPaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  amount: PropTypes.number.isRequired,
  recipient: PropTypes.string.isRequired,
  onPaymentSuccess: PropTypes.func.isRequired,
  onPaymentFailure: PropTypes.func.isRequired,
  paymentReason: PropTypes.string.isRequired,
  selectedGuild: PropTypes.string.isRequired,
  collectionBeingUnlocked: PropTypes.shape({
    collectionAddress: PropTypes.string.isRequired,
  }),
  subscriptionLevel: PropTypes.string,
  maxCollectionsAllowed: PropTypes.number.isRequired,
};

SolanaPaymentModal.defaultProps = {
  collectionBeingUnlocked: null,
  subscriptionLevel: 'basic',
};

export default SolanaPaymentModal;

